/**
 * Enhanced Verification Policy Center hooks (Issue #270).
 *
 * Wraps the typed `@jinzhuan/api-client` calls so the `/settings`
 * enhanced verification section can read the policy list / provider
 * readiness / fallback work orders / audit events, and submit PATCH
 * mutations.
 *
 * 红线：
 *   - 所有数据通过 `@jinzhuan/api-client` typed client 调用，禁止手写
 *     fetch / 假成功状态。
 *   - 写入成功后必须重新拉取后端列表，不依赖前端乐观状态；高风险表单不在前端
 *     伪装成功。
 *   - 任何非 2xx 错误必须冒泡给操作人；hooks 不静默吞错。
 */
import { useCallback, useEffect, useState } from 'react';
import {
  createApiClient,
  type EnhancedVerificationAuditEventDto,
  type EnhancedVerificationComplianceBlockerDto,
  type EnhancedVerificationFallbackWorkOrderDto,
  type EnhancedVerificationPolicyDto,
  type EnhancedVerificationProviderReadinessDto,
  type UpdateEnhancedVerificationPolicyRequest,
  type UpdateEnhancedVerificationProviderRequest,
} from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export interface AsyncState<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: string | null;
}

function pickErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const maybeMessage = (err as { message?: string }).message;
    if (typeof maybeMessage === 'string') return maybeMessage;
  }
  return fallback;
}

export interface EnhancedVerificationCenterReturn {
  readonly policies: AsyncState<readonly EnhancedVerificationPolicyDto[]>;
  readonly providers: AsyncState<readonly EnhancedVerificationProviderReadinessDto[]>;
  readonly fallbackWorkOrders: AsyncState<readonly EnhancedVerificationFallbackWorkOrderDto[]>;
  readonly auditEvents: AsyncState<readonly EnhancedVerificationAuditEventDto[]>;
  readonly complianceBlockers: AsyncState<
    readonly EnhancedVerificationComplianceBlockerDto[]
  >;
  refresh: () => Promise<void>;
  updatePolicy: (
    sceneKey: string,
    input: UpdateEnhancedVerificationPolicyRequest,
  ) => Promise<EnhancedVerificationPolicyDto>;
  updateProvider: (
    providerKey: string,
    input: UpdateEnhancedVerificationProviderRequest,
  ) => Promise<EnhancedVerificationProviderReadinessDto>;
}

export function useEnhancedVerificationCenter(): EnhancedVerificationCenterReturn {
  const [policies, setPolicies] = useState<AsyncState<readonly EnhancedVerificationPolicyDto[]>>({
    data: null,
    loading: false,
    error: null,
  });
  const [providers, setProviders] = useState<
    AsyncState<readonly EnhancedVerificationProviderReadinessDto[]>
  >({
    data: null,
    loading: false,
    error: null,
  });
  const [fallbackWorkOrders, setFallbackWorkOrders] = useState<
    AsyncState<readonly EnhancedVerificationFallbackWorkOrderDto[]>
  >({
    data: null,
    loading: false,
    error: null,
  });
  const [auditEvents, setAuditEvents] = useState<
    AsyncState<readonly EnhancedVerificationAuditEventDto[]>
  >({
    data: null,
    loading: false,
    error: null,
  });
  const [complianceBlockers, setComplianceBlockers] = useState<
    AsyncState<readonly EnhancedVerificationComplianceBlockerDto[]>
  >({
    data: null,
    loading: false,
    error: null,
  });

  const loadPolicies = useCallback(async () => {
    setPolicies((prev) => ({ ...prev, loading: true, error: null }));
    setComplianceBlockers((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { data, error } = await apiClient.GET(
        '/api/v1/admin/enhanced-verification/policies',
      );
      if (error) {
        const msg = pickErrorMessage(error, '加载增强验证策略失败');
        setPolicies({ data: null, loading: false, error: msg });
        setComplianceBlockers({ data: null, loading: false, error: msg });
        return;
      }
      setPolicies({
        data: data?.policies ?? [],
        loading: false,
        error: null,
      });
      setComplianceBlockers({
        data: data?.complianceBlockers ?? [],
        loading: false,
        error: null,
      });
    } catch (err) {
      const msg = pickErrorMessage(err, '加载增强验证策略失败');
      setPolicies({ data: null, loading: false, error: msg });
      setComplianceBlockers({ data: null, loading: false, error: msg });
    }
  }, []);

  const loadProviders = useCallback(async () => {
    setProviders((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { data, error } = await apiClient.GET(
        '/api/v1/admin/enhanced-verification/provider-readiness',
      );
      if (error) {
        setProviders({
          data: null,
          loading: false,
          error: pickErrorMessage(error, '加载人脸 provider 就绪状态失败'),
        });
        return;
      }
      setProviders({
        data: data?.providers ?? [],
        loading: false,
        error: null,
      });
    } catch (err) {
      setProviders({
        data: null,
        loading: false,
        error: pickErrorMessage(err, '加载人脸 provider 就绪状态失败'),
      });
    }
  }, []);

  const loadFallback = useCallback(async () => {
    setFallbackWorkOrders((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { data, error } = await apiClient.GET(
        '/api/v1/admin/enhanced-verification/fallback-work-orders',
        { params: { query: { limit: 50 } } },
      );
      if (error) {
        setFallbackWorkOrders({
          data: null,
          loading: false,
          error: pickErrorMessage(error, '加载 fallback 工单失败'),
        });
        return;
      }
      setFallbackWorkOrders({
        data: data?.rows ?? [],
        loading: false,
        error: null,
      });
    } catch (err) {
      setFallbackWorkOrders({
        data: null,
        loading: false,
        error: pickErrorMessage(err, '加载 fallback 工单失败'),
      });
    }
  }, []);

  const loadAudit = useCallback(async () => {
    setAuditEvents((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { data, error } = await apiClient.GET(
        '/api/v1/admin/enhanced-verification/audit-events',
        { params: { query: { limit: 50 } } },
      );
      if (error) {
        setAuditEvents({
          data: null,
          loading: false,
          error: pickErrorMessage(error, '加载增强验证审计事件失败'),
        });
        return;
      }
      setAuditEvents({
        data: data?.rows ?? [],
        loading: false,
        error: null,
      });
    } catch (err) {
      setAuditEvents({
        data: null,
        loading: false,
        error: pickErrorMessage(err, '加载增强验证审计事件失败'),
      });
    }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([loadPolicies(), loadProviders(), loadFallback(), loadAudit()]);
  }, [loadPolicies, loadProviders, loadFallback, loadAudit]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const updatePolicy = useCallback(
    async (sceneKey: string, input: UpdateEnhancedVerificationPolicyRequest) => {
      const { data, error } = await apiClient.PATCH(
        '/api/v1/admin/enhanced-verification/policies/{sceneKey}',
        { params: { path: { sceneKey } }, body: input },
      );
      if (error) {
        throw new Error(pickErrorMessage(error, '更新增强验证策略失败'));
      }
      if (!data) {
        throw new Error('服务端未返回更新后的策略');
      }
      await refresh();
      return data;
    },
    [refresh],
  );

  const updateProvider = useCallback(
    async (
      providerKey: string,
      input: UpdateEnhancedVerificationProviderRequest,
    ) => {
      const { data, error } = await apiClient.PATCH(
        '/api/v1/admin/enhanced-verification/provider-readiness/{providerKey}',
        { params: { path: { providerKey } }, body: input },
      );
      if (error) {
        throw new Error(
          pickErrorMessage(error, '更新人脸 provider 就绪状态失败'),
        );
      }
      if (!data) {
        throw new Error('服务端未返回更新后的 provider readiness');
      }
      await refresh();
      return data;
    },
    [refresh],
  );

  return {
    policies,
    providers,
    fallbackWorkOrders,
    auditEvents,
    complianceBlockers,
    refresh,
    updatePolicy,
    updateProvider,
  };
}

export type {
  EnhancedVerificationAuditEventDto,
  EnhancedVerificationComplianceBlockerDto,
  EnhancedVerificationFallbackWorkOrderDto,
  EnhancedVerificationPolicyDto,
  EnhancedVerificationProviderReadinessDto,
  UpdateEnhancedVerificationPolicyRequest,
  UpdateEnhancedVerificationProviderRequest,
};
